import { Button, Card, Space, Typography } from "antd";
import { DetailPageErrorState } from "@/views/components/DetailPageErrorState";
import { type ChangeEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid,
    Select as ZhSelect,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { LINE_PAYMENT_METHOD } from "@/meta/expenseManage/shippingDeposit";
import { YES_OR_NO } from "@/meta/common";
import {
    OWNER_SHIPPING_DEPOSIT_TYPE_QHY,
    PAY_TYPE,
    PAYMENT_STATUS,
    QHY_PAYMENT_STATE,
    SHIP_WAY_BILL_TYPE,
    WAYBILL_STATE,
    WAYBILL_TYPE
} from "@/meta/logistics/waybill";
import { useUserStore } from "@/views/stores";
import RequiredMark from "@/views/components/RequiredMark";
import { OWNER_DEPOSIT_STATE, OWNER_DEPOSIT_TYPE } from "@zhihaoscm/meta";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

interface WaybillServiceFeeDetail {
    payFileId?: number;
}

/** 旧 Vue 费用明细表使用的船主运费记录。 */
interface WaybillDepositRecord {
    id?: number | string;
    type?: number;
    amount?: number;
    state?: number;
    captainConfirmState?: number;
    paymentMethod?: number;
    paymentFileId?: number;
    receiptFileId?: number;
    remark?: string;
    approveTime?: string;
}

export interface WaybillManagementDetailData {
    transportOrderShip: {
        id: string | number;
        srpId?: string | number;
        type?: number;
        serviceType?: number;
        state?: number;
        goodsType?: string;
        ownerName?: string;
        ownerMobile?: string;
        ownerEnterprise?: { name?: string; institutionName?: string; realName?: string; code?: string };
        sourcePortName?: string;
        destinationPortName?: string;
        unitPrice?: number;
        ton?: number;
        actualFreightCost?: number;
        loadDate?: string;
        loadDays?: number;
        loadUnloadDays?: number;
        shipName?: string;
        shipType?: number;
        captainEnterprise?: { name?: string; institutionName?: string; realName?: string };
        captainName?: string;
        captainMobile?: string;
        upstreamHandlerName?: string;
        downstreamHandlerName?: string;
        shippingHandlerName?: string;
        createdTime?: string;
        captainShipInfoServiceFeeState?: number;
        ownerShipInfoServiceFee?: number;
        ownerShipInfoServiceFeePayType?: number;
        ownerShipInfoServiceFeeState?: number;
        captainShipInfoServiceFee?: number;
        captainShipInfoServiceFeePayType?: number;
        payType?: number;
        unloadingVideoId?: number;
        /** 船运单是否已完成发航确认，保留 Vue 详情 VO 字段。 */
        isSailConfirm?: number | boolean;
    };
    /** 装货/卸货节点信息来自 Vue 详情 VO 的嵌套明细。 */
    transportOrderDetailsShip?: {
        deliveryInfo?: { loadingDate?: string };
        lazbInfo?: { loadVideoId?: number; loadingCompletionTime?: string };
        setSailInfo?: { departureTime?: string };
        captainArrivalTime?: string;
        unloadingInfo?: {
            unloadingVideoId?: number;
            unloadingStartTime?: string;
            unloadingCompletionTime?: string;
            unloadingTonnage?: number;
        };
    };
    /** 服务费记录来自同一详情 VO，支付凭证不另发请求。 */
    owmerShipInfoServiceFee?: WaybillServiceFeeDetail;
    captainShipInfoServiceFee?: WaybillServiceFeeDetail;
    /** 运费支付明细仍由旧详情接口返回，并继续使用 zhihao 表格呈现。 */
    ownerShippingDeposits?: WaybillDepositRecord[];
    orderId?: string | number;
}

export interface WaybillManagementDetailPageProps {
    assignWaybill?: (
        id: string,
        params: { upstreamHandlerId?: string; downstreamHandlerId?: string }
    ) => Promise<unknown>;
    matchAssignWaybill?: (id: string, handlerId: string) => Promise<unknown>;
    loadSpecials?: (serviceSpecialsType?: number) => Promise<Array<{ id: number | string; name: string }>>;
    permissions?: string[];
    loadDetail?: (id: string) => Promise<WaybillManagementDetailData>;
    completeWaybill?: (id: string) => Promise<unknown>;
    closeWaybill?: (id: string) => Promise<unknown>;
    confirmArrival?: (id: string, fileId: number) => Promise<unknown>;
    uploadDeparture?: (
        id: string,
        params: { loadVideoId: number; tonProof?: number; loadingTonnage?: number }
    ) => Promise<unknown>;
    uploadUnloading?: (id: string, params: { unloadingVideoId: number }) => Promise<unknown>;
}

/** 复用 Vue 船运单详情接口，保持历史详情 VO 路径。 */
export async function loadWaybillManagementDetail(id: string): Promise<WaybillManagementDetailData> {
    const response = await shippingRequestHttp.service.get<WaybillManagementDetailData>(
        `/transport/order/ship/detail/${encodeURIComponent(id)}`
    );
    return response.data;
}

/** 复用 Vue 完成船运单接口。 */
export async function completeWaybillManagement(id: string) {
    const response = await shippingRequestHttp.service.put(`/transport/order/ship/complete/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用 Vue 关闭船运单接口。 */
export async function closeWaybillManagement(id: string) {
    const response = await shippingRequestHttp.service.delete(`/transport/order/ship/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用 Vue 到港确认接口，将上传后的文件 ID 作为 arrivalVideoId 提交。 */
export async function confirmWaybillArrival(id: string, fileId: number) {
    const response = await shippingRequestHttp.service.put(
        `/transport/order/ship/confirmation-arrival-port/${encodeURIComponent(id)}?arrivalVideoId=${fileId}`
    );
    return response.data;
}

/** 复用 Vue 装货信息接口，保留履约文件字段契约。 */
export async function uploadWaybillDeparture(
    id: string,
    params: { loadVideoId: number; tonProof?: number; loadingTonnage?: number }
) {
    const response = await shippingRequestHttp.service.put(
        `/transport/order/details/ship/update/departure-information/${encodeURIComponent(id)}`,
        params
    );
    return response.data;
}

/** 复用 Vue 卸货信息接口，保留 unloadingVideoId 字段契约。 */
export async function uploadWaybillUnloading(id: string, params: { unloadingVideoId: number }) {
    const response = await shippingRequestHttp.service.put(
        `/transport/order/details/ship/update/unloading-information/${encodeURIComponent(id)}`,
        params
    );
    return response.data;
}

/** 复用 Vue 服务运单指派专员查询接口。 */
export async function loadWaybillSpecials(serviceSpecialsType = 1) {
    const response = await shippingRequestHttp.service.get<Array<{ id: number | string; name: string }>>(
        `/transport/order/ship/find/specials?serviceSpecialsType=${serviceSpecialsType}`
    );
    return response.data;
}

/** 复用 Vue 服务运单指派接口，保留上下游专员 query 参数。 */
export async function assignWaybillManagement(
    id: string,
    params: { upstreamHandlerId?: string; downstreamHandlerId?: string }
) {
    const query = new URLSearchParams();
    if (params.upstreamHandlerId) query.set("upstreamHandlerId", params.upstreamHandlerId);
    if (params.downstreamHandlerId) query.set("downstreamHandlerId", params.downstreamHandlerId);
    const response = await shippingRequestHttp.service.put(
        `/transport/order/ship/assign/${encodeURIComponent(id)}${query.toString() ? `?${query}` : ""}`
    );
    return response.data;
}

/** 复用 Vue 撮合运单指派接口。 */
export async function matchAssignWaybillManagement(id: string, handlerId: string) {
    const response = await shippingRequestHttp.service.get(
        `/transport/order/ship/assign/${encodeURIComponent(id)}/${handlerId}`
    );
    return response.data;
}

const WAYBILL_IMAGE_EXTENSIONS = ["jpeg", "jpg", "png", "gif"];
const WAYBILL_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const WAYBILL_VIDEO_EXTENSIONS = ["mp4", "mov", "mkv"];
const WAYBILL_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-matroska"];

/** 对齐 Vue 船运单上传边界，签好运 payType=3 时只允许图片。 */
function validateWaybillFulfillmentFile(file: File, payType?: number): string | null {
    const extension = file.name.split(".").pop()?.toLowerCase();
    const isImage =
        extension != null && WAYBILL_IMAGE_EXTENSIONS.includes(extension) && WAYBILL_IMAGE_TYPES.includes(file.type);
    const isVideo =
        extension != null && WAYBILL_VIDEO_EXTENSIONS.includes(extension) && WAYBILL_VIDEO_TYPES.includes(file.type);
    if (payType === 3 && !isImage) return "仅支持图片文件（jpeg、jpg、png、gif）";
    if (!isImage && !isVideo) return "仅支持图片和视频文件（mp4、mov、mkv、jpeg、jpg、png、gif）";
    return null;
}

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function amountText(value?: number, suffix = ""): string {
    return typeof value === "number"
        ? `${value.toLocaleString("zh-CN", { minimumFractionDigits: 2 })}${suffix}`
        : DEFAULT_VALUE;
}

/** 格式化服务费金额，沿用 Vue 保留两位小数且不改变单位文案。 */
function serviceFeeText(value?: number): string {
    return typeof value === "number"
        ? `${value.toLocaleString("zh-CN", { minimumFractionDigits: 2 })}元`
        : DEFAULT_VALUE;
}

/** 将详情节点时间转换为旧 Vue 详情页使用的分钟精度。 */
function fulfillmentDateText(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

/** 格式化 Vue 详情中的企业名称和用户 ID，保持空值展示口径。 */
function enterpriseText(value?: { name?: string; institutionName?: string; realName?: string; code?: string }): string {
    const name = value?.name || value?.institutionName || value?.realName;
    return `${text(name)}（用户ID: ${text(value?.code)}）`;
}

interface WaybillFulfillmentInfoProps {
    state?: number;
    details?: WaybillManagementDetailData["transportOrderDetailsShip"];
}

/** 服务运单履约摘要，复用详情接口中的节点数据，不新增节点请求。 */
function WaybillFulfillmentInfo({ state, details }: WaybillFulfillmentInfoProps) {
    const unloadingInfo = details?.unloadingInfo;
    return (
        <Card title="履约信息">
            <ZhMultiGrid>
                <ZhGrid cols={4} gap={[50, 16]}>
                    <ZhBaseItem label="当前履约节点" value={WAYBILL_STATE.getIntroByCode(state) || DEFAULT_VALUE} />
                    <ZhBaseItem label="装货时间" value={fulfillmentDateText(details?.deliveryInfo?.loadingDate)} />
                    <ZhBaseItem
                        label="装货完成时间"
                        value={fulfillmentDateText(details?.lazbInfo?.loadingCompletionTime)}
                    />
                    <ZhBaseItem label="发航时间" value={fulfillmentDateText(details?.setSailInfo?.departureTime)} />
                    <ZhBaseItem label="到港时间" value={fulfillmentDateText(details?.captainArrivalTime)} />
                    <ZhBaseItem label="卸货开始时间" value={fulfillmentDateText(unloadingInfo?.unloadingStartTime)} />
                    <ZhBaseItem
                        label="卸货完成时间"
                        value={fulfillmentDateText(unloadingInfo?.unloadingCompletionTime)}
                    />
                    <ZhBaseItem label="卸货吨位" value={amountText(unloadingInfo?.unloadingTonnage, " 吨")} />
                </ZhGrid>
            </ZhMultiGrid>
        </Card>
    );
}

interface WaybillServiceFeeInfoProps {
    order: WaybillManagementDetailData["transportOrderShip"];
    ownerFee?: WaybillServiceFeeDetail;
    captainFee?: WaybillServiceFeeDetail;
}

/** 船务信息服务费摘要，保留货主/船主支付状态与线下支付凭证字段。 */
function WaybillServiceFeeInfo({ order, ownerFee, captainFee }: WaybillServiceFeeInfoProps) {
    const ownerOffline = order.ownerShipInfoServiceFeePayType === PAY_TYPE.OFFLINE.code;
    const captainOffline = order.captainShipInfoServiceFeePayType === PAY_TYPE.OFFLINE.code;
    return (
        <Card title="船务信息服务费信息">
            <ZhMultiGrid>
                <ZhGrid cols={4} gap={[50, 16]}>
                    <ZhBaseItem label="货主服务费" value={serviceFeeText(order.ownerShipInfoServiceFee)} />
                    <ZhBaseItem
                        label="支付方式"
                        value={PAY_TYPE.getIntroByCode(order.ownerShipInfoServiceFeePayType) || DEFAULT_VALUE}
                    />
                    <ZhBaseItem
                        label="货主支付状态"
                        value={
                            order.ownerShipInfoServiceFee
                                ? PAYMENT_STATUS.getIntroByCode(order.ownerShipInfoServiceFeeState) || DEFAULT_VALUE
                                : DEFAULT_VALUE
                        }
                    />
                    {ownerOffline ? <ZhBaseItem label="货主支付凭证" value={text(ownerFee?.payFileId)} /> : null}
                    <ZhBaseItem label="船主服务费" value={serviceFeeText(order.captainShipInfoServiceFee)} />
                    <ZhBaseItem
                        label="支付方式"
                        value={PAY_TYPE.getIntroByCode(order.captainShipInfoServiceFeePayType) || DEFAULT_VALUE}
                    />
                    <ZhBaseItem
                        label="船主支付状态"
                        value={
                            order.captainShipInfoServiceFee
                                ? PAYMENT_STATUS.getIntroByCode(order.captainShipInfoServiceFeeState) || DEFAULT_VALUE
                                : DEFAULT_VALUE
                        }
                    />
                    {captainOffline ? <ZhBaseItem label="船主支付凭证" value={text(captainFee?.payFileId)} /> : null}
                </ZhGrid>
            </ZhMultiGrid>
        </Card>
    );
}

interface WaybillDepositTableProps {
    payType?: number;
    rows: WaybillDepositRecord[];
}

/** 打开费用凭证，沿用旧 Vue 通过文件服务换取可访问地址的行为。 */
async function previewDepositFile(fileId?: number) {
    if (!fileId) return;
    const { fileService } = await import("@/api/services/fileService");
    const file = await fileService.getFileById(fileId);
    window.open(file.path, "_blank", "noopener,noreferrer");
}

/** 运单费用明细保留旧 Vue 的列、状态文案和总计，并由 zhihao-ui-react Table 完整渲染。 */
function WaybillDepositTable({ payType, rows }: WaybillDepositTableProps) {
    const isQhyPayment = payType === LINE_PAYMENT_METHOD.PAY_EASY_SIGN.code;
    const isOfflinePayment = payType === LINE_PAYMENT_METHOD.OFFLINE.code;
    const columns: TableColumnProps<WaybillDepositRecord>[] = [
        {
            label: "支付类型",
            prop: "type",
            render: ({ row }) =>
                (isQhyPayment ? OWNER_SHIPPING_DEPOSIT_TYPE_QHY : OWNER_DEPOSIT_TYPE).getIntroByCode(row.type) ||
                DEFAULT_VALUE
        },
        {
            label: "费用(元)",
            prop: "amount",
            render: ({ row }) => amountText(row.amount)
        },
        {
            label: isOfflinePayment ? "黄码港支付状态" : isQhyPayment ? "签好运支付状态" : "货主支付状态",
            prop: "state",
            render: ({ row }) =>
                (isQhyPayment ? QHY_PAYMENT_STATE : OWNER_DEPOSIT_STATE).getIntroByCode(row.state) || DEFAULT_VALUE
        },
        {
            label: "船主确认收款状态",
            prop: "captainConfirmState",
            render: ({ row }) => (row.captainConfirmState === YES_OR_NO.YES.code ? "已确认" : "未确认")
        },
        {
            label: "支付凭证",
            prop: "paymentFileId",
            render: ({ row }) => {
                const receiptFileId = row.receiptFileId;
                const paymentFileId = row.paymentFileId;
                if (isQhyPayment) {
                    return receiptFileId ? (
                        <Button type="link" onClick={() => void previewDepositFile(receiptFileId)}>
                            查看
                        </Button>
                    ) : (
                        DEFAULT_VALUE
                    );
                }
                if (!isOfflinePayment) {
                    return paymentFileId ? (
                        <Button type="link" onClick={() => void previewDepositFile(paymentFileId)}>
                            查看
                        </Button>
                    ) : (
                        DEFAULT_VALUE
                    );
                }
                if (!receiptFileId && !paymentFileId) return DEFAULT_VALUE;
                return (
                    <Space size={4}>
                        {receiptFileId ? (
                            <Button type="link" onClick={() => void previewDepositFile(receiptFileId)}>
                                电子回单
                            </Button>
                        ) : null}
                        {paymentFileId ? (
                            <Button type="link" onClick={() => void previewDepositFile(paymentFileId)}>
                                结算单
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        },
        ...(isOfflinePayment || isQhyPayment
            ? [
                  {
                      label: "审核结果",
                      prop: "remark",
                      render: ({ row }: { row: WaybillDepositRecord }) => text(row.remark)
                  } satisfies TableColumnProps<WaybillDepositRecord>
              ]
            : []),
        {
            label: "支付时间",
            prop: "approveTime",
            render: ({ row }) => dateText(row.approveTime)
        }
    ];
    const totalAmount = rows.reduce((total, row) => total + (row.amount || 0), 0);

    return (
        <Card title="费用明细">
            <ZhTable<WaybillDepositRecord>
                columns={columns}
                data={rows}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                pagination={false}
                rowKey={row => String(row.id ?? `${row.type}-${row.amount}-${row.approveTime}`)}
            />
            <div className="mt-4">
                <ZhBaseItem label="总计" value={amountText(totalAmount, "元")} />
            </div>
        </Card>
    );
}

/** 船运单详情，覆盖 Vue 基本信息、运输信息、船主信息和状态操作入口。 */
export function WaybillManagementDetailPage({
    assignWaybill = assignWaybillManagement,
    loadDetail = loadWaybillManagementDetail,
    loadSpecials = loadWaybillSpecials,
    matchAssignWaybill = matchAssignWaybillManagement,
    completeWaybill = completeWaybillManagement,
    closeWaybill = closeWaybillManagement,
    confirmArrival = confirmWaybillArrival,
    uploadDeparture = uploadWaybillDeparture,
    uploadUnloading = uploadWaybillUnloading,
    permissions
}: WaybillManagementDetailPageProps) {
    const { id: routeId } = useParams();
    const location = useLocation();
    const id = routeId || location.pathname.split("/").filter(Boolean).pop() || "";
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    // 未显式注入权限时保持旧详情页动作可见性，真实路由和显式权限仍执行访问控制。
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canAssign = permissions ? currentPermissions.includes("ROLE_SHIP_ORDER_MANAGE") : true;
    const [data, setData] = useState<WaybillManagementDetailData>();
    const [errorMessage, setErrorMessage] = useState("");
    const [arrivalUploadOpen, setArrivalUploadOpen] = useState(false);
    const [arrivalFileId, setArrivalFileId] = useState<number>();
    const [arrivalUploading, setArrivalUploading] = useState(false);
    const [fulfillmentType, setFulfillmentType] = useState<"loading" | "unloading">();
    const [fulfillmentUploading, setFulfillmentUploading] = useState(false);
    const [loadVideoId, setLoadVideoId] = useState<number>();
    const [tonProof, setTonProof] = useState<number>();
    const [loadingTonnage, setLoadingTonnage] = useState<number>();
    const [unloadingFileId, setUnloadingFileId] = useState<number>();
    const [assignOpen, setAssignOpen] = useState(false);
    const [specials, setSpecials] = useState<Array<{ id: number | string; name: string }>>([]);
    const [selectedSpecialId, setSelectedSpecialId] = useState("");

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (disposed) return;
                setData(result);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setData(undefined);
                setErrorMessage("船运单详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    useEffect(() => {
        if (!assignOpen) return;
        let disposed = false;
        void loadSpecials(1)
            .then(result => {
                if (!disposed) setSpecials(result || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("船务专员加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [assignOpen, loadSpecials]);

    if (errorMessage)
        return (
            <DetailPageErrorState
                ariaLabel="waybill-management-detail-page"
                message={errorMessage}
                onBack={() => navigate(-1)}
            />
        );
    if (!data) return <section aria-label="waybill-management-detail-page" />;

    const order = data.transportOrderShip;
    const state = Number(order.state);
    const freight = order.actualFreightCost || (order.unitPrice || 0) * (order.ton || 0);
    // 只有详情 VO 返回服务费字段时才展示区块，避免旧数据页面新增空白区域。
    const hasServiceFeeInfo = Boolean(
        data.owmerShipInfoServiceFee ||
        data.captainShipInfoServiceFee ||
        order.ownerShipInfoServiceFee != null ||
        order.captainShipInfoServiceFee != null
    );
    const existingLoadVideoId = data.transportOrderDetailsShip?.lazbInfo?.loadVideoId;
    const existingUnloadingVideoId =
        order.unloadingVideoId ?? data.transportOrderDetailsShip?.unloadingInfo?.unloadingVideoId;
    // 对齐 Vue 详情页，关闭动作同时受待付定金状态和独立关闭权限控制。
    const canClose =
        state === WAYBILL_STATE.PENDING_DEPOSIT.code && currentPermissions.includes("ROLE_SHIP_ORDER_CLOSE");
    const canComplete =
        state === WAYBILL_STATE.UNCOMPLETED.code ||
        (state === WAYBILL_STATE.TO_BE_UNLOADED.code && Boolean(existingUnloadingVideoId));
    /** 按 Vue 详情页条件保留装货、到港和卸货履约入口。 */
    const canShowLoadingInfo = state === WAYBILL_STATE.AWAITING_DEPARTURE.code && !existingLoadVideoId;
    const canShowArrivalConfirmation = state === WAYBILL_STATE.DURING_TRANSPORTATION.code;
    const canShowUnloadingInfo = state === WAYBILL_STATE.TO_BE_UNLOADED.code && !existingUnloadingVideoId;

    /** 沿用 Vue 服务/撮合运单分支，完成指派后刷新详情。 */
    const submitAssign = async () => {
        if (!selectedSpecialId) {
            setErrorMessage("请选择船务专员");
            return;
        }
        try {
            if (order.serviceType === SHIP_WAY_BILL_TYPE.MATCHING.code) {
                await matchAssignWaybill(id, selectedSpecialId);
            } else {
                await assignWaybill(id, { upstreamHandlerId: selectedSpecialId });
            }
            setAssignOpen(false);
            setData(await loadDetail(id));
            notifyOperationSuccess("已指派");
        } catch {
            setErrorMessage("船务专员指派失败");
        }
    };

    /** 操作成功后重新请求详情，保持 Vue 页面状态刷新行为。 */
    const runAction = async (action: () => Promise<unknown>, successMessage: string, errorText: string) => {
        try {
            await action();
            notifyOperationSuccess(successMessage);
            setData(await loadDetail(id));
        } catch {
            setErrorMessage(errorText);
        }
    };

    /** 选择到港履约文件，上传成功后回填 fileId 并提交到港确认回调。 */
    const handleArrivalFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = "";
        if (!file || arrivalUploading) return;

        const validationError = validateWaybillFulfillmentFile(file, order.payType);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }

        setArrivalUploading(true);
        setErrorMessage("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            // 上传服务只在用户选择履约文件后加载，避免详情只读回归被文件模块阻塞。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isFinite(fileId) || fileId <= 0) throw new Error("missing file id");
            await confirmArrival(id, fileId);
            setArrivalFileId(fileId);
            setArrivalUploadOpen(false);
            notifyOperationSuccess("到港确认成功");
        } catch {
            setErrorMessage("到港文件上传失败");
        } finally {
            setArrivalUploading(false);
        }
    };

    /** 按 Vue single-file-upload 规则校验履约文件，签好运 payType=3 仅允许图片。 */
    const handleFulfillmentFileChange = async (
        event: ChangeEvent<HTMLInputElement>,
        fileRole: "main" | "tonProof" = "main"
    ) => {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = "";
        if (!file || !fulfillmentType) return;
        const validationError = validateWaybillFulfillmentFile(file, order.payType);
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setErrorMessage("文件大小超过限制");
            return;
        }
        setFulfillmentUploading(true);
        setErrorMessage("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            // 履约上传按需加载文件服务，避免影响详情页只读回归。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isFinite(fileId) || fileId <= 0) throw new Error("missing file id");
            if (fulfillmentType === "loading" && fileRole === "tonProof") setTonProof(fileId);
            else if (fulfillmentType === "loading") setLoadVideoId(fileId);
            else setUnloadingFileId(fileId);
        } catch {
            setErrorMessage("履约文件上传失败");
        } finally {
            setFulfillmentUploading(false);
        }
    };

    /** 提交装货信息，字段与 Vue uploadDrainageInfo 完全一致。 */
    const submitDeparture = async () => {
        if (!loadVideoId || !tonProof || !loadingTonnage) {
            setErrorMessage("请完善装货信息");
            return;
        }
        try {
            await uploadDeparture(id, { loadVideoId, tonProof, loadingTonnage });
            notifyOperationSuccess("上传成功");
        } catch {
            setErrorMessage("装货信息提交失败");
        }
    };

    /** 提交卸货信息，字段与 Vue uploadUnloadingInfo 完全一致。 */
    const submitUnloading = async () => {
        if (!unloadingFileId) {
            setErrorMessage("请上传卸货文件");
            return;
        }
        try {
            await uploadUnloading(id, { unloadingVideoId: unloadingFileId });
            notifyOperationSuccess("上传成功");
        } catch {
            setErrorMessage("卸货信息提交失败");
        }
    };

    return (
        <section aria-label="waybill-management-detail-page" className="flex min-h-full flex-col gap-4">
            <ZhDetailHeader
                leftInfos={[
                    { label: "编号", value: text(order.id) },
                    {
                        label: "关联船运需求",
                        value: order.srpId ? (
                            <Link to={`/logistics/shippingNeed/platformShipNeedInfo/${order.srpId}`}>
                                {order.srpId}
                            </Link>
                        ) : (
                            DEFAULT_VALUE
                        )
                    },
                    { label: "关联订单", value: text(data.orderId) },
                    { label: "运单类型", value: WAYBILL_TYPE.getIntroByCode(order.type) || DEFAULT_VALUE },
                    { label: "服务类型", value: SHIP_WAY_BILL_TYPE.getIntroByCode(order.serviceType) || DEFAULT_VALUE }
                ]}
                rightInfos={[
                    { label: "船务专员", value: text(order.shippingHandlerName || order.upstreamHandlerName) },
                    { label: "创建时间", value: dateText(order.createdTime) }
                ]}
                stateDesc={WAYBILL_STATE.getIntroByCode(order.state) || DEFAULT_VALUE}
                title="船运单详情"
            />
            <Card title="基本信息">
                <ZhMultiGrid>
                    <ZhGrid cols={4} gap={[50, 16]}>
                        <ZhBaseItem label="货名" value={text(order.goodsType)} />
                        <ZhBaseItem label="货主" value={enterpriseText(order.ownerEnterprise)} />
                        <ZhBaseItem label="联系人" value={text(order.ownerName)} />
                        <ZhBaseItem label="手机号" value={text(order.ownerMobile)} />
                    </ZhGrid>
                </ZhMultiGrid>
            </Card>
            <Card title="运输信息">
                <ZhMultiGrid>
                    <ZhGrid cols={4} gap={[50, 16]}>
                        <ZhBaseItem label="始发地" value={text(order.sourcePortName)} />
                        <ZhBaseItem label="目的地" value={text(order.destinationPortName)} />
                        <ZhBaseItem label="运价" value={amountText(order.unitPrice, " 元/吨")} />
                        <ZhBaseItem label="运货吨数" value={amountText(order.ton, " 吨")} />
                        <ZhBaseItem label="预估运费" value={amountText(freight, " 元")} />
                        <ZhBaseItem label="装载日期" value={`${dateText(order.loadDate)} +${text(order.loadDays)}天`} />
                        <ZhBaseItem label="两港装卸天数" value={text(order.loadUnloadDays)} />
                    </ZhGrid>
                </ZhMultiGrid>
            </Card>
            <Card title="船主信息">
                <ZhMultiGrid>
                    <ZhGrid cols={4} gap={[50, 16]}>
                        <ZhBaseItem label="船舶" value={text(order.shipName?.split("/")[0])} />
                        <ZhBaseItem label="承运商" value={enterpriseText(order.captainEnterprise)} />
                        <ZhBaseItem label="联系人" value={text(order.captainName)} />
                        <ZhBaseItem label="手机号" value={text(order.captainMobile)} />
                    </ZhGrid>
                </ZhMultiGrid>
            </Card>
            {order.serviceType === SHIP_WAY_BILL_TYPE.SUPERVISION.code ? (
                <WaybillFulfillmentInfo state={order.state} details={data.transportOrderDetailsShip} />
            ) : null}
            {hasServiceFeeInfo ? (
                <WaybillServiceFeeInfo
                    order={order}
                    ownerFee={data.owmerShipInfoServiceFee}
                    captainFee={data.captainShipInfoServiceFee}
                />
            ) : null}
            {data.ownerShippingDeposits ? (
                <WaybillDepositTable payType={order.payType} rows={data.ownerShippingDeposits} />
            ) : null}
            <Card title="历史记录">
                <Typography.Text type="secondary">暂无操作记录</Typography.Text>
            </Card>
            <Card>
                <Space>
                    {state !== WAYBILL_STATE.COMPLETED.code && canAssign ? (
                        <Button
                            aria-label="指派"
                            disabled={banButton}
                            onClick={() => {
                                setSelectedSpecialId("");
                                setAssignOpen(true);
                            }}
                        >
                            指派
                        </Button>
                    ) : null}
                    {canShowLoadingInfo ? (
                        <>
                            <Button aria-label="装货信息" onClick={() => setFulfillmentType("loading")}>
                                装货信息
                            </Button>
                            {fulfillmentType === "loading" ? (
                                <>
                                    <label>
                                        <span>
                                            <RequiredMark />
                                            装载吨位
                                        </span>
                                        <input
                                            aria-label="装载吨位"
                                            type="number"
                                            onChange={event => setLoadingTonnage(Number(event.target.value))}
                                        />
                                    </label>
                                    <label>
                                        <span>
                                            <RequiredMark />
                                            {order.payType === 3 ? "满载图片" : "满载图片或视频"}
                                        </span>
                                        <input
                                            aria-label={order.payType === 3 ? "满载图片" : "满载图片或视频"}
                                            accept={
                                                order.payType === 3
                                                    ? ".jpeg,.jpg,.png,.gif,image/jpeg,image/png,image/gif"
                                                    : ".mp4,.mov,.mkv,.jpeg,.jpg,.png,.gif,video/mp4,video/quicktime,video/x-matroska,image/jpeg,image/png,image/gif"
                                            }
                                            type="file"
                                            onChange={event => void handleFulfillmentFileChange(event)}
                                        />
                                    </label>
                                    <label>
                                        <span>
                                            <RequiredMark />
                                            吨位证明
                                        </span>
                                        <input
                                            aria-label="吨位证明"
                                            accept=".jpeg,.jpg,.png,.gif,image/jpeg,image/png,image/gif"
                                            type="file"
                                            onChange={event => {
                                                setFulfillmentType("loading");
                                                void handleFulfillmentFileChange(event, "tonProof");
                                            }}
                                        />
                                    </label>
                                    {loadVideoId != null ? <span>装货文件 ID：{loadVideoId}</span> : null}
                                    <Button
                                        aria-label="确认装货信息"
                                        disabled={fulfillmentUploading}
                                        onClick={() => void submitDeparture()}
                                    >
                                        确认装货信息
                                    </Button>
                                </>
                            ) : null}
                        </>
                    ) : null}
                    {canShowArrivalConfirmation ? (
                        <>
                            <Button
                                aria-label="到港确认"
                                disabled={arrivalUploading}
                                onClick={() => {
                                    setErrorMessage("");
                                    setArrivalUploadOpen(true);
                                }}
                            >
                                到港确认
                            </Button>
                            {arrivalUploadOpen ? (
                                <label>
                                    <span>
                                        <RequiredMark />
                                        到港确认文件
                                    </span>
                                    <input
                                        aria-label="到港确认文件"
                                        accept={
                                            order.payType === 3
                                                ? ".jpeg,.jpg,.png,.gif,image/jpeg,image/png,image/gif"
                                                : ".mp4,.mov,.mkv,.jpeg,.jpg,.png,.gif,video/mp4,video/quicktime,video/x-matroska,image/jpeg,image/png,image/gif"
                                        }
                                        disabled={arrivalUploading}
                                        type="file"
                                        onChange={event => void handleArrivalFileChange(event)}
                                    />
                                </label>
                            ) : null}
                            {arrivalFileId != null ? <span>到港文件 ID：{arrivalFileId}</span> : null}
                        </>
                    ) : null}
                    {canShowUnloadingInfo ? (
                        <>
                            <Button aria-label="卸货信息" onClick={() => setFulfillmentType("unloading")}>
                                卸货信息
                            </Button>
                            {fulfillmentType === "unloading" ? (
                                <>
                                    <label>
                                        <span>
                                            <RequiredMark />
                                            {order.payType === 3 ? "卸货图片" : "卸货图片或视频"}
                                        </span>
                                        <input
                                            aria-label={order.payType === 3 ? "卸货图片" : "卸货图片或视频"}
                                            accept={
                                                order.payType === 3
                                                    ? ".jpeg,.jpg,.png,.gif,image/jpeg,image/png,image/gif"
                                                    : ".mp4,.mov,.mkv,.jpeg,.jpg,.png,.gif,video/mp4,video/quicktime,video/x-matroska,image/jpeg,image/png,image/gif"
                                            }
                                            type="file"
                                            onChange={event => void handleFulfillmentFileChange(event)}
                                        />
                                    </label>
                                    {unloadingFileId != null ? <span>卸货文件 ID：{unloadingFileId}</span> : null}
                                    <Button
                                        aria-label="确认卸货信息"
                                        disabled={fulfillmentUploading}
                                        onClick={() => void submitUnloading()}
                                    >
                                        确认卸货信息
                                    </Button>
                                </>
                            ) : null}
                        </>
                    ) : null}
                    {canClose ? (
                        <Button
                            aria-label="关闭"
                            onClick={() => void runAction(() => closeWaybill(id), "已关闭", "船运单关闭失败")}
                        >
                            关闭
                        </Button>
                    ) : null}
                    {canComplete ? (
                        <Button
                            aria-label="完成"
                            onClick={() => void runAction(() => completeWaybill(id), "已完成", "船运单完成失败")}
                        >
                            完成
                        </Button>
                    ) : null}
                    <Button aria-label="返回" type="primary" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
            <ZhDialog
                open={assignOpen}
                title="指派服务专员"
                width={400}
                onCancel={() => setAssignOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignOpen(false)}>取消</Button>
                        <Button
                            aria-label="确认指派"
                            disabled={!selectedSpecialId || banButton}
                            type="primary"
                            onClick={() => void submitAssign()}
                        >
                            确认指派
                        </Button>
                    </Space>
                }
            >
                <label>
                    <span>
                        <RequiredMark />
                        船务专员
                    </span>
                    <ZhSelect
                        aria-label="船务专员"
                        className="w-full"
                        options={specials.map(special => ({
                            value: String(special.id),
                            label: special.name
                        }))}
                        value={selectedSpecialId}
                        placeholder="请选择船务专员"
                        showSearch
                        onSearch={value => {
                            const matchingSpecial = specials.find(special => String(special.id) === value);
                            if (matchingSpecial) setSelectedSpecialId(String(matchingSpecial.id));
                        }}
                        onChange={value => setSelectedSpecialId(String(value || ""))}
                    />
                </label>
            </ZhDialog>
        </section>
    );
}

export default WaybillManagementDetailPage;
